import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch('https://readdy.ai/api/form/d7ajqfv6e3lk97049u10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTo = (href: string) => {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer id="footer" className="bg-[#080503] pt-16 md:pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
        {/* Top grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12 mb-12 md:mb-16">

          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-4 md:mb-5">
              <img
                src="https://storage.readdy-site.link/project_files/f7a91cb2-16ed-4f1e-969d-5ece724c23c9/b03cb20b-4fb4-4837-9a55-89db62a7f1e0_equiIconPlain.png?v=4f72fd85e284e63ce2e4f62caa853a60"
                alt="Equi AS"
                className="w-9 h-9 md:w-10 md:h-10 object-contain"
                width="40"
                height="40"
                loading="lazy"
              />
              <div>
                <p className="text-white font-bold text-base md:text-lg tracking-widest uppercase">Equi AS</p>
                <p className="text-[#c9a84c] text-[10px] tracking-[0.2em] uppercase">Skjønnhet og Velvære</p>
              </div>
            </div>
            <p className="text-white/40 text-sm leading-relaxed mb-5 md:mb-6 max-w-xs">
              En unik og koselig klinikk i Rudshøgda sentrum med moderne behandlinger tilpasset deg.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/EQUInorway"
                target="_blank"
                rel="nofollow noreferrer"
                aria-label="Equi AS på Facebook"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-white/15 text-white/50 hover:border-[#c9a84c]/50 hover:text-[#c9a84c] transition-all duration-300 cursor-pointer"
              >
                <i className="ri-facebook-fill text-sm" />
              </a>
              <a
                href="https://www.instagram.com/equivelvaeret/"
                target="_blank"
                rel="nofollow noreferrer"
                aria-label="Equi AS på Instagram"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-white/15 text-white/50 hover:border-[#c9a84c]/50 hover:text-[#c9a84c] transition-all duration-300 cursor-pointer"
              >
                <i className="ri-instagram-line text-sm" />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <p className="text-[#c9a84c] text-xs tracking-[0.2em] uppercase font-medium mb-4 md:mb-5">Navigasjon</p>
            <ul className="space-y-2.5 md:space-y-3">
              {[
                { label: 'Hjem', href: '#hero' },
                { label: 'Tjenester', href: '#services' },
                { label: 'Om Oss', href: '#about' },
                { label: 'Online Booking', href: 'https://system.easypractice.net/book/equi#choose-where', external: true },
              ].map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'nofollow noreferrer' : undefined}
                    onClick={!link.external ? (e) => {
                      e.preventDefault();
                      scrollTo(link.href);
                    } : undefined}
                    className="text-white/50 text-sm hover:text-[#c9a84c] transition-colors duration-200 cursor-pointer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className="text-[#c9a84c] text-xs tracking-[0.2em] uppercase font-medium mb-4 md:mb-5">Kontakt</p>
            <ul className="space-y-3 md:space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-[#c9a84c] text-sm" />
                </div>
                <address className="text-white/50 text-sm leading-relaxed not-italic">
                  Bogstivegen 72,<br />2360 Rudshøgda
                </address>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-[#c9a84c] text-sm" />
                </div>
                <a href="tel:+4746884700" className="text-white/50 text-sm hover:text-[#c9a84c] transition-colors">+47 468 84 700</a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-[#c9a84c] text-sm" />
                </div>
                <a href="mailto:minequi@hotmail.com" className="text-white/50 text-sm hover:text-[#c9a84c] transition-colors break-all">minequi@hotmail.com</a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-time-line text-[#c9a84c] text-sm" />
                </div>
                <div className="text-white/50 text-sm leading-relaxed">
                  <p>Man–Fre: 09:00–20:00</p>
                  <p>Lørdag: 10:00–16:00</p>
                  <p>Søndag: Stengt</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <p className="text-[#c9a84c] text-xs tracking-[0.2em] uppercase font-medium mb-4 md:mb-5">Nyhetsbrev</p>
            <p className="text-white/40 text-sm leading-relaxed mb-4 md:mb-5">
              Få eksklusive tilbud, skjønnhetstips og nyheter rett i innboksen din.
            </p>
            {submitted ? (
              <div className="flex items-center gap-2 text-[#c9a84c] text-sm">
                <i className="ri-checkbox-circle-line" />
                <span>Takk! Du er nå påmeldt.</span>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-3">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Din e-postadresse"
                  required
                  autoComplete="email"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#c9a84c]/50 transition-colors"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-4 py-3 bg-[#c9a84c] text-[#0e0a07] font-semibold rounded-xl text-sm hover:bg-[#e0c06a] transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap active:scale-95"
                >
                  {submitting ? 'Sender...' : 'Abonner'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-white/8 mb-6 md:mb-8" />

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 md:gap-4">
          <p className="text-white/25 text-xs text-center sm:text-left">© 2025 Equi AS. Alle rettigheter forbeholdt.</p>
          <p className="text-white/25 text-xs text-center sm:text-right">Bogstivegen 72, 2360 Rudshøgda, Norge</p>
        </div>
      </div>
    </footer>
  );
}